import { errorMessage } from "@lumora/bridge";
import { ViewModel } from "../mvvm/ViewModel.js";
/** Platform console: site list, live ops data (orders, events, traffic) and site accounts. */
export class SiteOpsViewModel extends ViewModel {
    client;
    constructor(client) {
        super({
            siteLoad: { status: "loading" },
            ordersEnabled: false,
            orders: [],
            events: [],
            usage: null,
            siteUsage: null,
            storageQuota: null,
            users: null,
            sites: null,
            sitesError: null,
            message: null,
            error: null,
        });
        this.client = client;
    }
    async loadSites() {
        try {
            this.setState({ sites: await this.client.listSites(), sitesError: null });
        }
        catch (e) {
            this.setState({ sitesError: errorMessage(e, "Failed to load sites") });
        }
    }
    /** Resolve one site from the admin list, and whether it takes orders. */
    async loadSite(slug) {
        this.setState({ siteLoad: { status: "loading" }, ordersEnabled: false });
        const siteP = this.client.listSites().then((sites) => {
            const site = sites.find((s) => s.slug === slug);
            this.setState({ siteLoad: site ? { status: "ready", site } : { status: "notFound" } });
        }, () => this.setState({ siteLoad: { status: "error" } }));
        const ordersP = this.client.getSite(slug).then((s) => this.setState({ ordersEnabled: s.ordersEnabled === true }), () => this.setState({ ordersEnabled: false }));
        await Promise.all([siteP, ordersP]);
    }
    /** Throws on failure so a LivePanel can surface it. Returns the rows for callers that diff them. */
    async loadOrders(slug) {
        const orders = await this.client.listSiteOrders(slug);
        this.setState({ orders });
        return orders;
    }
    clearOrders() {
        this.setState({ orders: [] });
    }
    async loadEvents(limit, tenant) {
        this.setState({
            events: await this.client.listAdminEvents(limit, tenant ? { tenant } : undefined),
        });
    }
    async loadUsage(slug) {
        const [usage, siteUsage] = await Promise.all([
            this.client.getPlatformUsage(),
            this.client.getAdminSiteUsage(slug),
        ]);
        this.setState({ usage, siteUsage });
    }
    async loadStorageQuota(slug) {
        try {
            this.setState({ storageQuota: await this.client.getSiteStorageQuota(slug) });
        }
        catch (e) {
            this.setState({ error: errorMessage(e, "Failed to load storage quota") });
        }
    }
    async saveStorageQuota(slug, input) {
        try {
            this.setState({
                storageQuota: await this.client.setSiteStorageQuota(slug, input),
                error: null,
            });
        }
        catch (e) {
            this.setState({ error: errorMessage(e, "Failed to save storage quota") });
        }
    }
    async setFounder(slug, founder) {
        try {
            const res = await this.client.setSiteFounder(slug, founder);
            const load = this.state.siteLoad;
            const patch = { error: null };
            if (load.status === "ready" && load.site.slug === slug) {
                patch.siteLoad = { status: "ready", site: { ...load.site, founder: res.founder } };
            }
            if (this.state.sites) {
                patch.sites = this.state.sites.map((s) => (s.slug === slug ? { ...s, founder: res.founder } : s));
            }
            this.setState(patch);
        }
        catch (e) {
            this.setState({ error: errorMessage(e, "Failed to update founder flag") });
        }
    }
    placeTestOrder(slug, target) {
        return this.client.placeTestOrder(slug, target);
    }
    async loadUsers(slug) {
        try {
            this.setState({ users: await this.client.listSiteUsers(slug) });
        }
        catch (e) {
            this.setState({ error: errorMessage(e, "Failed to load accounts") });
        }
    }
    clearStatus() {
        this.setState({ message: null, error: null });
    }
    /** Runs an account mutation: clears status, sets `message` on success or `error` on failure. */
    async account(fn, ok, fallback, slug, reload) {
        this.clearStatus();
        try {
            const r = await fn();
            this.setState({ message: ok(r) });
            if (reload)
                await this.loadUsers(slug);
            return true;
        }
        catch (e) {
            this.setState({ error: errorMessage(e, fallback) });
            return false;
        }
    }
    requestPasswordReset(slug, userId, resetBaseUrl, describe) {
        return this.account(() => this.client.adminRequestUserPasswordReset(slug, userId, { resetBaseUrl }), describe, "Failed to send reset", slug, false);
    }
    changeRole(slug, userId, role) {
        return this.account(() => this.client.adminPatchSiteUser(slug, userId, { role }), (u) => `${u.email} is now ${u.role}.`, "Failed to change role", slug, true);
    }
    createOwner(slug, email, password) {
        return this.account(() => this.client.adminCreateOwner(slug, { email, password }), (u) => `Owner created: ${u.email}`, "Failed to create owner", slug, true);
    }
    createAccount(slug, email, password, role) {
        return this.account(() => this.client.adminCreateSiteUser(slug, { email, password, role }), (u) => `Account created: ${u.email} (${u.role}).`, "Failed to add account", slug, true);
    }
}
//# sourceMappingURL=SiteOpsViewModel.js.map